import { Checkbox } from '@/components/ui/checkbox';
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@/components/ui/select';
import type { DisciplineWithCourses, DivisionWithCourses } from '@/lib/types';
import { courseGrades } from '@/Shared/Data/StaticData';

import type { ReportFilters } from './reportUtils';

const ALL_VALUE = '__all__';

const filterSelectItemClassName =
	'pl-2 pr-8 [&>span:first-child]:left-auto [&>span:first-child]:right-2';

type ReportFiltersProps = {
	filters: ReportFilters;
	onChange: (next: ReportFilters) => void;
	disciplines: DisciplineWithCourses[];
	divisions: DivisionWithCourses[];
};

const ReportFiltersBar = ({ filters, onChange, disciplines, divisions }: ReportFiltersProps) => {
	const gradeOptions = courseGrades.filter((g) => g.value !== '');

	return (
		<div className="flex flex-wrap items-end gap-3">
			<div className="flex flex-col gap-1 min-w-[160px]">
				<span className="text-xs text-(--header-text)">Discipline</span>
				<Select
					value={filters.disciplineId || ALL_VALUE}
					onValueChange={(value) =>
						onChange({
							...filters,
							disciplineId: value === ALL_VALUE ? '' : value,
						})
					}
				>
					<SelectTrigger
						className="h-9 w-[180px] justify-between text-left"
						aria-label="Filter by discipline"
					>
						<SelectValue placeholder="All disciplines" />
					</SelectTrigger>
					<SelectContent className="max-h-60">
						<SelectItem value={ALL_VALUE} className={filterSelectItemClassName}>
							All disciplines
						</SelectItem>
						{disciplines.map((discipline) => (
							<SelectItem
								key={discipline.id}
								value={discipline.id}
								className={filterSelectItemClassName}
							>
								{discipline.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="flex flex-col gap-1 min-w-[160px]">
				<span className="text-xs text-(--header-text)">Division</span>
				<Select
					value={filters.divisionId || ALL_VALUE}
					onValueChange={(value) =>
						onChange({
							...filters,
							divisionId: value === ALL_VALUE ? '' : value,
						})
					}
				>
					<SelectTrigger
						className="h-9 w-[180px] justify-between text-left"
						aria-label="Filter by division"
					>
						<SelectValue placeholder="All divisions" />
					</SelectTrigger>
					<SelectContent className="max-h-60">
						<SelectItem value={ALL_VALUE} className={filterSelectItemClassName}>
							All divisions
						</SelectItem>
						{divisions.map((division) => (
							<SelectItem
								key={division.id}
								value={division.id}
								className={filterSelectItemClassName}
							>
								{division.name}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<div className="flex flex-col gap-1 min-w-[140px]">
				<span className="text-xs text-(--header-text)">Grade</span>
				<Select
					value={filters.grade || ALL_VALUE}
					onValueChange={(value) =>
						onChange({
							...filters,
							grade: value === ALL_VALUE ? '' : value,
						})
					}
				>
					<SelectTrigger
						className="h-9 w-[140px] justify-between text-left"
						aria-label="Filter by grade"
					>
						<SelectValue placeholder="All grades" />
					</SelectTrigger>
					<SelectContent className="max-h-60">
						<SelectItem value={ALL_VALUE} className={filterSelectItemClassName}>
							All grades
						</SelectItem>
						{gradeOptions.map((grade) => (
							<SelectItem key={grade.id} value={grade.value} className={filterSelectItemClassName}>
								{grade.label}
							</SelectItem>
						))}
					</SelectContent>
				</Select>
			</div>

			<label
				htmlFor="include-archived"
				className="flex items-center gap-2 h-9 text-sm text-(--body-text) cursor-pointer"
			>
				<Checkbox
					id="include-archived"
					checked={filters.includeArchived}
					onCheckedChange={(checked) => onChange({ ...filters, includeArchived: checked === true })}
					aria-label="Include archived courses"
				/>
				Include archived
			</label>
		</div>
	);
};

export default ReportFiltersBar;
